import { type FC, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useEventCancelProgressQuery,
  useRetryEventCancelMutation,
} from "@/partner/entities/products";
import { useReconcileRefundMutation } from "@/partner/entities/refunds";
import { useAbility } from "@/partner/shared/lib";
import { PAYMENT_PROVIDER_LABEL_KEY } from "@/partner/shared/utils/paymentProvider";
import type { EventCancelItemFieldsFragmentType } from "@/types/graphql/partner.generated";
import { formatAmountObject } from "@/partner/shared/utils/formatCurrency";

const POLL_INTERVAL_MS = 5000;
const RETRY_POLL_MS = 2 * 60 * 1000;

interface EventCancelProgressCardProps {
  productId: string;
}

/**
 * Сверка неподтверждённого возврата Paynet (ответ вендора потерян): партнёр
 * проверяет возврат в бэк-офисе Paynet и отмечает итог, затем прогон
 * отмены запускается снова.
 */
const ReconcileButtons: FC<{
  refundId: string;
  onReconciled(): void;
}> = ({ refundId, onReconciled }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [reconcile, { loading }] = useReconcileRefundMutation({
    onCompleted: () => {
      enqueueSnackbar(t("products.eventCancel.reconcile.success"), {
        variant: "success",
      });
      onReconciled();
    },
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });
  return (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        disabled={loading}
        onClick={() =>
          void reconcile({ variables: { refundId, refunded: true } })
        }
      >
        {t("products.eventCancel.reconcile.refunded")}
      </Button>
      <Button
        size="small"
        color="inherit"
        disabled={loading}
        onClick={() =>
          void reconcile({ variables: { refundId, refunded: false } })
        }
      >
        {t("products.eventCancel.reconcile.notRefunded")}
      </Button>
    </Stack>
  );
};

const ItemsTable: FC<{
  title: string;
  items: EventCancelItemFieldsFragmentType[];
  withError?: boolean;
  canReconcile?: boolean;
  onReconciled?: () => void;
}> = ({
  title,
  items,
  withError = false,
  canReconcile = false,
  onReconciled,
}) => {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{title}</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("products.eventCancel.columns.buyer")}</TableCell>
              <TableCell>
                {t("products.eventCancel.columns.provider")}
              </TableCell>
              <TableCell>{t("products.eventCancel.columns.amount")}</TableCell>
              <TableCell>
                {t("products.eventCancel.columns.paymentId")}
              </TableCell>
              {withError && (
                <TableCell>{t("products.eventCancel.columns.error")}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.purchaseId}>
                <TableCell>{item.buyerEmail ?? item.purchaseId}</TableCell>
                <TableCell>
                  {item.provider
                    ? t(PAYMENT_PROVIDER_LABEL_KEY[item.provider])
                    : ""}
                </TableCell>
                <TableCell>{formatAmountObject(item.amount)}</TableCell>
                <TableCell>{item.providerPaymentId ?? ""}</TableCell>
                {withError && (
                  <TableCell>
                    <Stack spacing={1}>
                      <span>
                        {item.pendingRefundId
                          ? t("products.eventCancel.reconcile.hint")
                          : (item.error ?? "")}
                      </span>
                      {item.pendingRefundId && canReconcile && (
                        <ReconcileButtons
                          refundId={item.pendingRefundId}
                          onReconciled={() => onReconciled?.()}
                        />
                      )}
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

/**
 * Ход отмены события в карточке продукта: «возвращено N из M, ошибок K»,
 * неудачные возвраты и ручные списками, «Повторить неудачные». Пока отмена
 * не завершена, запрос опрашивается каждые 5 секунд.
 */
export const EventCancelProgressCard: FC<EventCancelProgressCardProps> = ({
  productId,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const ability = useAbility();

  const { data, stopPolling, startPolling } = useEventCancelProgressQuery({
    variables: { productId },
    fetchPolicy: "network-only",
    pollInterval: POLL_INTERVAL_MS,
  });
  const progress = data?.eventCancelProgress;
  const finished = Boolean(progress?.finishedAt);

  // Опрос нужен, только пока покупки ещё в очереди: конец отмены или одни
  // неудачи (им нужен человек, а не опрос) его останавливают; повтор
  // перезапрашивает ход и, если очередь снова не пуста, опрос возобновляется.
  // После повтора две минуты опрашиваем и без очереди: неудачные ещё не
  // взяты прогоном, а результат повтора партнёр ждёт на экране.
  const [retriedAt, setRetriedAt] = useState<number | null>(null);
  useEffect(() => {
    if (retriedAt === null) return undefined;
    const timer = setTimeout(() => setRetriedAt(null), RETRY_POLL_MS);
    return () => clearTimeout(timer);
  }, [retriedAt]);
  const active =
    retriedAt !== null ||
    (Boolean(progress) && !finished && (progress?.pending ?? 0) > 0);
  useEffect(() => {
    if (active) startPolling(POLL_INTERVAL_MS);
    else stopPolling();
  }, [active, startPolling, stopPolling]);

  const [retry, { loading: retrying }] = useRetryEventCancelMutation({
    variables: { productId },
    refetchQueries: ["EventCancelProgress"],
    onCompleted: () => {
      setRetriedAt(Date.now());
      enqueueSnackbar(t("products.eventCancel.progress.retrySuccess"), {
        variant: "success",
      });
    },
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  if (!progress) return null;

  // Повтор доступен, пока есть что обрабатывать: неудачные или зависшие
  // в очереди (прогон мог потеряться при сбое Redis).
  const canRetry =
    ability.can("create", "EventCancel") &&
    (progress.failed > 0 || (!finished && progress.pending > 0));
  const done =
    progress.refunded + progress.manual + progress.free + progress.disputed;
  const value =
    progress.total > 0
      ? ((done + progress.failed) / progress.total) * 100
      : 100;

  return (
    <Card elevation={0} sx={{ mb: 1 }}>
      <CardHeader
        sx={{ px: 2, pt: 2, pb: 0 }}
        title={t("products.eventCancel.progress.title")}
        subheader={
          finished
            ? t("products.eventCancel.progress.finished", {
                date: progress.finishedAt,
              })
            : t("products.eventCancel.progress.inProgress")
        }
        action={
          canRetry ? (
            <Button
              variant="outlined"
              disabled={retrying}
              onClick={() => void retry()}
            >
              {progress.failed > 0
                ? t("products.eventCancel.progress.retry")
                : t("products.eventCancel.progress.resume")}
            </Button>
          ) : null
        }
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Stack spacing={2}>
          <Typography>
            {t("products.eventCancel.progress.summary", {
              done,
              total: progress.total,
              failed: progress.failed,
            })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={value}
            color={progress.failed > 0 ? "warning" : "primary"}
          />
          <Typography variant="body2" color="text.secondary">
            {t("products.eventCancel.progress.details", {
              refunded: progress.refunded,
              manual: progress.manual,
              free: progress.free,
              disputed: progress.disputed,
              pending: progress.pending,
            })}
          </Typography>
          {progress.message && (
            <Typography variant="body2">
              {t("products.eventCancel.progress.message", {
                message: progress.message,
              })}
            </Typography>
          )}
          <ItemsTable
            title={t("products.eventCancel.progress.failuresTitle")}
            items={progress.failures}
            withError
            canReconcile={ability.can("create", "EventCancel")}
            onReconciled={() => void retry()}
          />
          <ItemsTable
            title={t("products.eventCancel.progress.manualTitle")}
            items={progress.manualItems}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};
